import { useEffect, useState } from 'react';
import { api, formatBRL, type AdminAccount } from '../api.js';
import { AdminAi } from './AdminAi.js';
import { PageHead } from './PageHead.js';

/**
 * Painel do administrador da plataforma.
 *
 * De propósito, ele não reimplementa nenhuma tela de cliente: lista as contas
 * e ENTRA na que precisar — a sessão vira a do cliente, com aviso fixo e botão
 * de voltar. Espelhar as telas aqui dobraria cada manutenção futura.
 */

type Props = {
  onEnterAccount: (token: string, nome: string) => void;
};

type Secao = 'contas' | 'ia';

/** Duas seções: as contas dos clientes e a IA da plataforma. */
function Abas({ secao, onTroca }: { secao: Secao; onTroca: (s: Secao) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
      <button
        type="button"
        className="tab"
        aria-current={secao === 'contas' ? 'page' : undefined}
        onClick={() => onTroca('contas')}
      >
        🏢 Contas
      </button>
      <button
        type="button"
        className="tab"
        aria-current={secao === 'ia' ? 'page' : undefined}
        onClick={() => onTroca('ia')}
      >
        🤖 IA e crédito
      </button>
    </div>
  );
}

const PROVIDER_LABEL: Record<string, string> = {
  SHOPEE: 'Shopee',
  INSTAGRAM: 'Instagram',
  TIKTOK: 'TikTok',
  WHATSAPP: 'WhatsApp',
};

export function AdminPanel({ onEnterAccount }: Props) {
  const [contas, setContas] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState<string | null>(null);
  const [secao, setSecao] = useState<Secao>('contas');

  useEffect(() => {
    void api
      .adminAccounts()
      .then(setContas)
      .catch((cause: unknown) =>
        setErro(cause instanceof Error ? cause.message : 'Falha ao listar as contas'),
      )
      .finally(() => setLoading(false));
  }, []);

  async function entrar(conta: AdminAccount) {
    setEntrando(conta.id);
    setErro(null);
    try {
      const r = await api.adminEnter(conta.id);
      onEnterAccount(r.token, r.conta.nome);
    } catch (cause) {
      setErro(cause instanceof Error ? cause.message : 'Falha ao entrar na conta');
      setEntrando(null);
    }
  }

  const totalComissao = contas.reduce((t, c) => t + c.comissaoTotalCents, 0);

  if (secao === 'ia') {
    return (
      <>
        <PageHead
          eyebrow="Plataforma"
          title="IA e crédito"
          description="A chave da Anthropic, quanto crédito resta e quanto cada cliente consumiu. Trocar a chave aqui vale na hora, sem deploy."
        />
        <Abas secao={secao} onTroca={setSecao} />
        <AdminAi />
      </>
    );
  }

  return (
    <>
      <PageHead
        eyebrow="Plataforma"
        title="Contas de clientes"
        description="Cada conta é um cliente do serviço. Entrar abre o painel dele numa sessão de uma hora, marcada como acesso do administrador."
        readout={[
          { label: contas.length === 1 ? 'cliente' : 'clientes', value: contas.length },
          contas.length > 0 && {
            label: 'de comissão somada',
            value: formatBRL(totalComissao),
            tom: totalComissao > 0 ? ('gain' as const) : ('quiet' as const),
          },
        ]}
      />

      <Abas secao={secao} onTroca={setSecao} />

      {erro && <div className="notice err">{erro}</div>}

      {loading ? (
        <div className="card empty">
          <span className="spinner" /> Carregando…
        </div>
      ) : contas.length === 0 ? (
        <div className="card empty">
          <strong>Nenhum cliente ainda</strong>
          As contas criadas no cadastro aparecem aqui.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {contas.map((c) => {
            const dono = c.usuarios[0];
            const conectadas = c.integracoes.filter((i) => i.status === 'CONNECTED');
            return (
              <section key={c.id} className="card" style={{ padding: 16 }}>
                <div className="row" style={{ alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{c.nome}</div>
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      {dono ? `${dono.name} · ${dono.email}` : 'sem usuário'}
                      {' · '}cliente desde{' '}
                      {new Date(c.criadaEm).toLocaleDateString('pt-BR')}
                      {dono?.lastLoginAt &&
                        ` · último acesso ${new Date(dono.lastLoginAt).toLocaleDateString('pt-BR')}`}
                    </div>
                    <div className="row" style={{ gap: 6, marginTop: 8 }}>
                      {conectadas.length === 0 ? (
                        <span className="badge warn">nenhuma integração</span>
                      ) : (
                        conectadas.map((i) => (
                          <span key={i.provider} className="badge ok">
                            {PROVIDER_LABEL[i.provider] ?? i.provider}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  <div
                    className="row"
                    style={{ gap: 18, flex: '0 0 auto', textAlign: 'right' }}
                  >
                    <Metrica rotulo="comissão" valor={formatBRL(c.comissaoTotalCents)} />
                    <Metrica rotulo="ofertas" valor={String(c.contagens.products)} />
                    <Metrica rotulo="posts" valor={String(c.contagens.posts)} />
                  </div>

                  <button
                    type="button"
                    className="primary"
                    style={{ flex: '0 0 auto' }}
                    disabled={entrando !== null}
                    onClick={() => void entrar(c)}
                  >
                    {entrando === c.id ? <span className="spinner" /> : 'Entrar na conta'}
                  </button>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

function Metrica({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <span style={{ flex: '0 0 auto' }}>
      <span className="num" style={{ fontWeight: 600, display: 'block', fontSize: 14 }}>
        {valor}
      </span>
      <span className="muted" style={{ fontSize: 11 }}>
        {rotulo}
      </span>
    </span>
  );
}
